<!--
  Общая секция НДС для доменных форм.
  Держит одинаковый UI/UX для типа НДС, ставки и способа расчета:
  "С НДС" подставляет дефолтную ставку, "НДС 0%" выставляет ставку 0,
  ручной ввод ставки синхронизирует тип. Payload и доменная валидация
  остаются в родительской форме.
-->
<template>
  <!-- Тип НДС -->
  <div :class="fieldClass">
    <base-label
      v-if="useBaseLabel"
      :required="true"
      :filled="vatKindFilled"
    >
      {{ vatKindLabel }}
    </base-label>
    <base-label
      v-else
      :class="labelClass"
      :required="true"
      :filled="vatKindFilled"
    >
      {{ vatKindLabel }}
    </base-label>
    <base-select
      :model-value="vatKind"
      :options="vatKindOptions"
      :clearable="false"
      :placeholder="vatKindPlaceholder"
      :invalid="Boolean(errors.vat_kind)"
      @change="handleVatKindChange"
    />
    <span v-if="errors.vat_kind" class="error-message">
      {{ vatKindErrorText }}
    </span>
  </div>

  <!-- Ставка НДС -->
  <div :class="fieldClass">
    <base-label v-if="useBaseLabel">{{ vatRateLabel }}</base-label>
    <base-label v-else :class="labelClass">
      {{ vatRateLabel }}
    </base-label>
    <base-input
      :model-value="vatRate"
      type="number"
      step="0.01"
      :min="0"
      :max="100"
      :placeholder="vatRatePlaceholder"
      :invalid="Boolean(errors.vat_rate)"
      :disabled="isVatRateDisabled"
      @update:modelValue="handleVatRateInput"
    />
    <span v-if="errors.vat_rate" class="error-message">
      {{ vatRateErrorText }}
    </span>
    <span
      v-if="vatKind === 'no_vat' && showNoVatHint"
      class="hint-message"
    >
      НДС не применяется
    </span>
  </div>

  <!-- Способ расчёта НДС -->
  <div :class="fieldClass">
    <base-label
      v-if="useBaseLabel"
      :required="true"
      :filled="vatCalculationTypeFilled"
    >
      {{ vatCalculationTypeLabel }}
    </base-label>
    <base-label
      v-else
      :class="labelClass"
      :required="true"
      :filled="vatCalculationTypeFilled"
    >
      {{ vatCalculationTypeLabel }}
    </base-label>
    <base-select
      :model-value="vatCalculationType"
      :options="vatCalculationTypeOptions"
      :clearable="false"
      :placeholder="vatCalculationTypePlaceholder"
      :invalid="Boolean(errors.vat_calculation_type)"
      @change="handleVatCalculationTypeChange"
    />
    <span
      v-if="errors.vat_calculation_type"
      class="error-message"
    >
      {{ vatCalculationTypeErrorText }}
    </span>
  </div>
</template>

<script>
import {
  nextVatKindForRateInput,
  nextVatRateForKind,
} from "@/utils/vatUtils";

export default {
  name: "VatFields",

  props: {
    vatKind: {
      type: String,
      default: null,
    },
    vatRate: {
      type: [String, Number],
      default: "",
    },
    vatCalculationType: {
      type: String,
      default: null,
    },
    vatKindOptions: {
      type: Array,
      required: true,
    },
    vatCalculationTypeOptions: {
      type: Array,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    fieldClass: {
      type: String,
      default: "form-group",
    },
    labelClass: {
      type: String,
      default: "",
    },
    useBaseLabel: {
      type: Boolean,
      default: true,
    },
    vatKindFilled: {
      type: Boolean,
      default: false,
    },
    vatCalculationTypeFilled: {
      type: Boolean,
      default: false,
    },
    vatKindLabel: {
      type: String,
      default: "Тип НДС",
    },
    vatRateLabel: {
      type: String,
      default: "Ставка НДС (%)",
    },
    vatCalculationTypeLabel: {
      type: String,
      default: "Способ расчёта НДС",
    },
    vatKindPlaceholder: {
      type: String,
      default: "Выберите тип НДС",
    },
    vatCalculationTypePlaceholder: {
      type: String,
      default: "Выберите способ",
    },
    vatKindErrorText: {
      type: String,
      default: "Выберите тип НДС",
    },
    vatRateErrorText: {
      type: String,
      default: "Ставка НДС должна быть от 0 до 100",
    },
    vatCalculationTypeErrorText: {
      type: String,
      default: "Выберите способ расчёта НДС",
    },
    showNoVatHint: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    "clear-error",
    "update:vatKind",
    "update:vatRate",
    "update:vatCalculationType",
  ],

  computed: {
    vatRatePlaceholder() {
      if (this.vatKind === "with_vat") return "0.00";
      if (this.vatKind === "zero") return "0";

      return "—";
    },

    isVatRateDisabled() {
      return this.vatKind === "no_vat";
    },
  },

  methods: {
    handleVatKindChange(option) {
      const kind = option ? option.value : null;
      this.$emit("update:vatKind", kind);
      this.$emit("clear-error", "vat_kind");
      this.$emit("clear-error", "vat_rate");

      const nextRate = nextVatRateForKind(kind, this.vatRate);
      if (nextRate !== null) this.$emit("update:vatRate", nextRate);
    },

    handleVatRateInput(value) {
      this.$emit("update:vatRate", value);
      this.$emit("clear-error", "vat_rate");

      const nextKind = nextVatKindForRateInput(this.vatKind, value);
      if (nextKind !== this.vatKind) {
        this.$emit("update:vatKind", nextKind);
        this.$emit("clear-error", "vat_kind");
      }
    },

    handleVatCalculationTypeChange(option) {
      this.$emit(
        "update:vatCalculationType",
        option ? option.value : null
      );
      this.$emit("clear-error", "vat_calculation_type");
    },
  },
};
</script>

<style scoped>
.catalog-item-form__field,
.form-group,
.inline-form-group,
.repair-item-row__field {
  min-width: 0;
}

.catalog-item-form__field,
.form-group,
.inline-form-group {
  display: flex;
  flex-direction: column;
}

.repair-item-row__label {
  display: none;
}

.error-message {
  display: block;
  margin-top: 5px;
  color: var(--color-danger);
  font-size: 12px;
  line-height: 1.2;
}

.hint-message {
  display: block;
  margin-top: 4px;
  color: #6b7280;
  font-size: 12px;
}

@media (min-width: 761px) {
  .repair-item-row__field .repair-item-row__label {
    display: block;
    margin-bottom: 5px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
  }
}

@media (max-width: 760px) {
  .repair-item-row__label {
    display: block;
    margin-bottom: 5px;
    color: #6b7280;
    font-size: 12px;
    font-weight: 600;
  }
}
</style>
